// Session 147j+15 — Predictability Audit panel.
//
// Asks the question: "Did the OI Flow notional triggers actually predict
// Nifty futures direction today?"
//
// Backend endpoint: GET /api/admin/oi-flow/predictability
//   Returns hit-rate / expectancy by horizon, per-trade table, and
//   per-minute Pearson r between (signed notional CR, arrow delta, volume)
//   and forward N-min Nifty future return.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Activity, RefreshCw, TrendingUp, TrendingDown, Target, BarChart3,
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = 'token';
const authHeaders = () => {
  const t = localStorage.getItem(TOKEN_KEY);
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const todayIST = () => {
  const d = new Date();
  const ist = new Date(d.getTime() + (5.5 * 60 - d.getTimezoneOffset()) * 60 * 1000);
  return ist.toISOString().slice(0, 10);
};

const fmtPct = (v) => (v == null ? '—' : (v >= 0 ? '+' : '') + Number(v).toFixed(3) + '%');
const fmtR = (v) => (v == null ? '—' : Number(v).toFixed(3));
const fmtRate = (v) => (v == null ? '—' : (Number(v) * 100).toFixed(1) + '%');

const Stat = ({ label, value, tone = 'text-foreground', testid }) => (
  <div className="flex flex-col" data-testid={testid}>
    <span className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">
      {label}
    </span>
    <span className={`text-sm font-mono ${tone}`}>{value}</span>
  </div>
);

const HorizonCard = ({ horizon, stats }) => {
  if (!stats) return null;
  const hitRate = stats.hit_rate;
  const tone =
    hitRate == null
      ? 'border-border'
      : hitRate >= 0.6
      ? 'border-emerald-500/50 bg-emerald-500/5'
      : hitRate >= 0.5
      ? 'border-amber-500/40 bg-amber-500/5'
      : 'border-rose-500/50 bg-rose-500/5';
  return (
    <div
      className={`p-3 border ${tone}`}
      data-testid={`predictability-horizon-${horizon}`}
    >
      <div className="text-[11px] font-mono uppercase tracking-wider text-foreground mb-2 flex items-center gap-1.5">
        <Target className="w-3 h-3" /> +{horizon}min · {stats.n} trades
      </div>
      <div className="grid grid-cols-2 gap-2">
        <Stat
          label="Hit rate"
          value={fmtRate(hitRate)}
          tone={
            hitRate == null
              ? 'text-muted-foreground'
              : hitRate >= 0.6
              ? 'text-emerald-300'
              : hitRate >= 0.5
              ? 'text-amber-300'
              : 'text-rose-300'
          }
          testid={`predictability-hit-rate-${horizon}`}
        />
        <Stat
          label="Expectancy"
          value={fmtPct(stats.expectancy_pct)}
          tone={
            stats.expectancy_pct == null
              ? 'text-muted-foreground'
              : stats.expectancy_pct > 0
              ? 'text-emerald-300'
              : 'text-rose-300'
          }
          testid={`predictability-expectancy-${horizon}`}
        />
        <Stat
          label="Avg favorable"
          value={fmtPct(stats.mean_favorable_pct)}
          tone="text-emerald-300/80"
        />
        <Stat
          label="Avg adverse"
          value={fmtPct(stats.mean_adverse_pct)}
          tone="text-rose-300/80"
        />
      </div>
    </div>
  );
};

const CorrelationRow = ({ horizon, corr }) => {
  const colorR = (r) =>
    r == null
      ? 'text-muted-foreground'
      : Math.abs(r) >= 0.3
      ? 'text-emerald-300'
      : Math.abs(r) >= 0.15
      ? 'text-amber-300'
      : 'text-muted-foreground';
  return (
    <tr className="border-t border-border/40">
      <td className="px-2 py-1 font-mono text-[11px]">+{horizon}min</td>
      <td className={`px-2 py-1 text-right font-mono text-[11px] ${colorR(corr.notional_r)}`}>
        {fmtR(corr.notional_r)}
      </td>
      <td className={`px-2 py-1 text-right font-mono text-[11px] ${colorR(corr.arrows_r)}`}>
        {fmtR(corr.arrows_r)}
      </td>
      <td className={`px-2 py-1 text-right font-mono text-[11px] ${colorR(corr.volume_r)}`}>
        {fmtR(corr.volume_r)}
      </td>
      <td className="px-2 py-1 text-right font-mono text-[11px] text-muted-foreground">
        {corr.n}
      </td>
    </tr>
  );
};

const TradeRow = ({ trade, horizons }) => {
  const isBull = trade.kind === 'BULL';
  return (
    <tr
      className="border-t border-border/40 hover:bg-surface-highlight/40"
      data-testid={`predictability-trade-${trade.ist_minute}`}
    >
      <td className="px-2 py-1 font-mono text-[11px] text-muted-foreground">
        {trade.ist_minute?.slice(11, 16)}
      </td>
      <td className="px-2 py-1">
        <span
          className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-mono uppercase border ${
            isBull
              ? 'border-emerald-500/50 bg-emerald-500/10 text-emerald-300'
              : 'border-rose-500/50 bg-rose-500/10 text-rose-300'
          }`}
        >
          {isBull ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
          {trade.kind}
        </span>
      </td>
      <td className="px-2 py-1 text-right font-mono text-[11px]">{trade.match_strikes}</td>
      <td className="px-2 py-1 text-right font-mono text-[11px] text-emerald-300/70">
        {trade.ce_total_cr?.toFixed(1)}
      </td>
      <td className="px-2 py-1 text-right font-mono text-[11px] text-rose-300/70">
        {trade.pe_total_cr?.toFixed(1)}
      </td>
      <td className="px-2 py-1 text-right font-mono text-[11px] text-foreground">
        {trade.entry_close?.toFixed(1)}
      </td>
      {horizons.map((h) => {
        const f = trade.fwd?.[String(h)] || {};
        const hit = f.hit;
        return (
          <td
            key={h}
            className={`px-2 py-1 text-right font-mono text-[11px] ${
              hit == null
                ? 'text-muted-foreground'
                : hit
                ? 'text-emerald-300'
                : 'text-rose-300'
            }`}
            title={f.close ? `close ${f.close}` : 'no future candle'}
          >
            {f.ret_pct == null
              ? '—'
              : (f.ret_pct >= 0 ? '+' : '') + f.ret_pct.toFixed(2) + '%'}
            {hit != null ? (hit ? ' ✓' : ' ✗') : ''}
          </td>
        );
      })}
    </tr>
  );
};

const PredictabilityAudit = ({ symbol = 'NIFTY' }) => {
  const [date, setDate] = useState(todayIST());
  const [horizons, setHorizons] = useState('5,15,30,60');
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await axios.get(
        `${API}/api/admin/oi-flow/predictability` +
          `?symbol=${symbol}&date=${date}&horizons=${horizons}`,
        { headers: authHeaders() },
      );
      setData(r.data);
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message;
      setError(msg);
      toast.error(`Audit failed: ${msg}`);
    } finally {
      setBusy(false);
    }
  }, [symbol, date, horizons]);

  useEffect(() => {
    load();
  }, [load]);

  const horizonList = useMemo(
    () =>
      (data?.horizons || horizons.split(','))
        .map((h) => Number(h))
        .filter((h) => !Number.isNaN(h)),
    [data, horizons],
  );

  const ta = data?.trigger_audit;
  const trades = ta?.trades || [];

  return (
    <div
      className="border border-border bg-surface p-3 space-y-3"
      data-testid="predictability-audit-card"
    >
      <div className="flex items-center gap-2 flex-wrap">
        <div className="text-[11px] font-mono uppercase tracking-wider text-foreground flex items-center gap-1.5">
          <BarChart3 className="w-3 h-3" /> Predictability audit · {symbol}
        </div>
        <div className="ml-auto flex items-center gap-2">
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="bg-surface-highlight border border-border px-2 py-1 text-[11px] font-mono text-foreground"
            data-testid="predictability-date-input"
          />
          <input
            type="text"
            value={horizons}
            onChange={(e) => setHorizons(e.target.value)}
            placeholder="5,15,30,60"
            className="bg-surface-highlight border border-border px-2 py-1 text-[11px] font-mono text-foreground w-28"
            data-testid="predictability-horizons-input"
            title="Comma-separated forward minutes"
          />
          <button
            onClick={load}
            disabled={busy}
            className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-mono uppercase tracking-wider border border-border hover:border-accent hover:text-accent disabled:opacity-50"
            data-testid="predictability-run-btn"
          >
            <RefreshCw className={`w-3 h-3 ${busy ? 'animate-spin' : ''}`} />
            Run
          </button>
        </div>
      </div>

      <p className="text-[10px] font-mono text-muted-foreground leading-relaxed">
        For each notional-bias trigger fired today, computes the Nifty
        futures forward return at +H minutes. <span className="text-foreground">Hit-rate</span> = % of triggers
        whose direction matched the futures move. <span className="text-foreground">Pearson r</span> below
        measures per-minute correlation across all minutes (not just trigger
        bars).
      </p>

      {error && (
        <div className="px-2 py-1 text-[11px] font-mono text-rose-300 border border-rose-500/40 bg-rose-500/10">
          {error}
        </div>
      )}

      {data && (
        <>
          {/* Diagnostic banner */}
          <div className="flex items-center gap-3 flex-wrap text-[10px] font-mono text-muted-foreground">
            <span>
              <Activity className="inline w-3 h-3 mr-1" />
              {data.diagnostics.candles} candles · {data.diagnostics.signals} OI rows · {' '}
              <span className="text-foreground">{data.diagnostics.triggers_audited}</span> triggers audited
            </span>
            {ta && (
              <span>
                <span className="text-emerald-300">{ta.n_bull} BULL</span> ·{' '}
                <span className="text-rose-300">{ta.n_bear} BEAR</span>
              </span>
            )}
            {data.diagnostics.notes?.map((n, i) => (
              <span key={i} className="text-amber-300">⚠ {n}</span>
            ))}
          </div>

          {/* Hit-rate cards by horizon */}
          {ta && ta.n_total > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
              {horizonList.map((h) => (
                <HorizonCard
                  key={h}
                  horizon={h}
                  stats={ta.by_horizon[String(h)]}
                />
              ))}
            </div>
          )}

          {/* Per-trade table */}
          {trades.length > 0 && (
            <div className="overflow-x-auto" data-testid="predictability-trades-table">
              <table className="w-full text-[11px] font-mono">
                <thead className="text-muted-foreground uppercase tracking-wider">
                  <tr>
                    <th className="px-2 py-1 text-left">Time</th>
                    <th className="px-2 py-1 text-left">Bias</th>
                    <th className="px-2 py-1 text-right">Strikes</th>
                    <th className="px-2 py-1 text-right">CE ₹Cr</th>
                    <th className="px-2 py-1 text-right">PE ₹Cr</th>
                    <th className="px-2 py-1 text-right">Entry</th>
                    {horizonList.map((h) => (
                      <th key={h} className="px-2 py-1 text-right">+{h}m</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {trades.map((t) => (
                    <TradeRow key={t.ist_minute} trade={t} horizons={horizonList} />
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Correlation table */}
          {data.correlation && Object.keys(data.correlation).length > 0 && (
            <div data-testid="predictability-correlation-table">
              <div className="text-[11px] font-mono uppercase tracking-wider text-foreground mt-2 mb-1">
                Per-minute correlation (Pearson r)
              </div>
              <table className="w-full text-[11px] font-mono">
                <thead className="text-muted-foreground uppercase tracking-wider">
                  <tr>
                    <th className="px-2 py-1 text-left">Horizon</th>
                    <th className="px-2 py-1 text-right">Notional flow → ret</th>
                    <th className="px-2 py-1 text-right">Arrows → ret</th>
                    <th className="px-2 py-1 text-right">Volume → |ret|</th>
                    <th className="px-2 py-1 text-right">N</th>
                  </tr>
                </thead>
                <tbody>
                  {horizonList.map((h) => (
                    <CorrelationRow
                      key={h}
                      horizon={h}
                      corr={data.correlation[String(h)] || {}}
                    />
                  ))}
                </tbody>
              </table>
              <div className="mt-1 text-[10px] font-mono text-muted-foreground">
                |r| ≥ 0.30 = useful predictive edge · 0.15-0.30 = weak ·
                &lt; 0.15 = noise. Sign tells direction.
              </div>
            </div>
          )}

          {ta && ta.n_total === 0 && (
            <div className="text-[11px] font-mono text-muted-foreground py-3">
              No notional-bias triggers fired on this date.
              {data.diagnostics.signals > 0 &&
                ' Per-minute correlation table above still shows whether the underlying signal had any predictive power.'}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default PredictabilityAudit;
